import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ContentModalService } from '@msi/cobalt-prime';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, Subject, take } from 'rxjs';
import { VerifyInput } from '@geocode-models/verify-input.model';
import { VerifyResult } from '@geocode-models/verify-result.model';
import { GeocodingRestService } from '@services/rest/geocoding-rest.service';
import { ToastServiceManager } from '@services/toast-service-manager.service';
import {
  SelectGeocodedAddressModalComponent,
  SelectGeocodedAddressModalData,
} from '../select-geocoded-address-modal/select-geocoded-address-modal.component';
import {
  AddressSuggestDialogComponent,
  AddressSuggestDialogResult,
} from './address-suggest-dialog/address-suggest-dialog.component';
import { AddressGeocodeDialogComponent } from './address-geocode-dialog/address-geocode-dialog.component';

@Injectable({ providedIn: 'root' })
export class PremiseHazardItemGeocodingService {
  constructor(
    private readonly geocodingService: GeocodingRestService,
    private readonly toastServiceManager: ToastServiceManager,
    private readonly contentModalService: ContentModalService,
    private readonly dialogService: DialogService,
    private readonly transloco: TranslocoService,
  ) {}

  public openSuggestPicker(): Observable<VerifyResult> {
    const out$ = new Subject<VerifyResult>();
    const ref = this.dialogService.open(AddressSuggestDialogComponent, {
      header: this.transloco.translate('PremiseHazard.Item.modal.lookup.header'),
      modal: true,
      closable: false,
      dismissableMask: false,
      closeOnEscape: true,
      width: '600px',
    });
    ref.onClose.pipe(take(1)).subscribe((result: AddressSuggestDialogResult) => {
      if (!result) {
        out$.complete();
        return;
      }
      if (result.action === 'validate') {
        this.runVerify({
          address: result.address.addressMatch,
          magicKey: result.address.magicKey,
          city: undefined,
          zipCode: undefined,
          building: undefined,
          apartment: undefined,
          floor: undefined,
          subdivision: undefined,
          locationName: undefined,
        }).subscribe({
          next: (r) => out$.next(r),
          complete: () => out$.complete(),
        });
      } else if (result.action === 'advancedSearch') {
        this.openAdvancedGeocode().subscribe({
          next: (r) => out$.next(r),
          complete: () => out$.complete(),
        });
      } else {
        out$.complete();
      }
    });
    return out$.asObservable();
  }

  public openAdvancedGeocode(): Observable<VerifyResult> {
    const out$ = new Subject<VerifyResult>();
    const ref = this.dialogService.open(AddressGeocodeDialogComponent, {
      header: this.transloco.translate('PremiseHazard.Item.modal.advancedLookup.header'),
      modal: true,
      closable: false,
      dismissableMask: false,
      closeOnEscape: false,
      width: '700px',
      contentStyle: { 'padding-top': '0' },
    });
    ref.onClose.pipe(take(1)).subscribe((result: VerifyInput | null) => {
      if (!result) {
        out$.complete();
        return;
      }
      this.runVerify(result).subscribe({
        next: (r) => out$.next(r),
        complete: () => out$.complete(),
      });
    });
    return out$.asObservable();
  }

  private runVerify(addressInput: VerifyInput): Observable<VerifyResult> {
    const out$ = new Subject<VerifyResult>();
    this.geocodingService
      .verify(addressInput)
      .pipe(take(1))
      .subscribe((response: VerifyResult[]) => {
        const matched = response.filter(this.filterValidLocations);
        if (!matched || matched.length === 0) {
          this.toastServiceManager.warning(
            this.transloco.translate('PremiseHazard.Item.toast.noValidLocations'),
          );
          out$.complete();
          return;
        }
        const contentModal = this.contentModalService.open<SelectGeocodedAddressModalData, number>({
          content: SelectGeocodedAddressModalComponent,
          data: { matchedLocations: matched },
        });
        contentModal
          .afterClosed()
          .pipe(take(1))
          .subscribe((idx: number | null | undefined) => {
            if (idx === undefined || idx === null) {
              this.toastServiceManager.warning(
                this.transloco.translate('PremiseHazard.Item.toast.noValidLocations'),
              );
              out$.complete();
              return;
            }
            if (idx < 0 || idx >= matched.length) {
              this.toastServiceManager.error(
                this.transloco.translate('PremiseHazard.Item.toast.noLocationSelected'),
              );
              out$.complete();
              return;
            }
            out$.next(matched[idx]);
            out$.complete();
          });
      });
    return out$.asObservable();
  }

  private filterValidLocations(element: VerifyResult): boolean {
    return !!element.matchLocation.x && !!element.matchLocation.y;
  }
}
